if (!customElements.get('lens-addon-picker')) {
  customElements.define(
    'lens-addon-picker',
    class LensAddonPicker extends HTMLElement {
      constructor() {
        super();

        this.variants = JSON.parse(this.dataset.variants || '[]');
        this.framePrice = parseInt(this.dataset.framePrice, 10) || 0;
        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

        this.variantInput = this.querySelector('[data-lens-variant-input]');
        this.noteInput = this.querySelector('[data-lens-note-input]');
        this.rxNoteElement = this.querySelector('[data-lens-rx-note]');
        this.prescriptionNote = this.dataset.prescriptionNote || '';
        this.totalElement = this.querySelector('[data-lens-total]');
        this.typesWrapper = this.querySelector('[data-lens-types]');
        this.tintsWrapper = this.querySelector('[data-lens-tints]');
        this.tintNameElement = this.querySelector('[data-lens-tint-name]');
        this.exampleElement = this.querySelector('[data-lens-example]');
        this.exampleImage = this.querySelector('[data-lens-example-image]');
        this.exampleCaption = this.querySelector('[data-lens-example-caption]');
        this.rxInputs = Array.from(this.querySelectorAll('[data-lens-rx-input]'));
        this.typeInputs = Array.from(this.querySelectorAll('[data-lens-type-input]'));
        this.tintInputs = Array.from(this.querySelectorAll('[data-lens-tint-input]'));

        [...this.rxInputs, ...this.typeInputs, ...this.tintInputs].forEach((input) =>
          input.addEventListener('change', this.onSelectionChange.bind(this))
        );

        // There's no "frame only" button: clicking the chosen prescription option again clears it.
        this.selectedRx = this.checkedValue(this.rxInputs);
        this.rxInputs.forEach((input) =>
          input.addEventListener('click', () => {
            if (input.value === this.selectedRx) input.checked = false;
            this.selectedRx = this.checkedValue(this.rxInputs);
            this.onSelectionChange();
          })
        );

        this.onSelectionChange();
      }

      checkedValue(inputs) {
        const checked = inputs.find((input) => input.checked && !input.disabled);
        return checked ? checked.value : '';
      }

      onSelectionChange() {
        const rx = this.checkedValue(this.rxInputs);
        const rxVariants = this.variants.filter((variant) => variant.rx === rx);

        // Prescription lenses: tell the customer right away that we'll email them to collect it.
        if (this.rxNoteElement) this.rxNoteElement.hidden = rx === '' || rx === 'No RX';

        // Lens type: only types that exist for the chosen prescription.
        this.typesWrapper.hidden = rx === '';
        this.typeInputs.forEach((input) => {
          const typeVariants = rxVariants.filter((variant) => variant.type === input.value);
          const option = input.closest('[data-lens-type-option]');
          option.hidden = typeVariants.length === 0;
          input.disabled = typeVariants.length === 0;
          if (input.disabled) input.checked = false;
        });

        // Tint: only tints that exist for the chosen prescription + type.
        const type = this.checkedValue(this.typeInputs);
        const typeVariants = rxVariants.filter((variant) => variant.type === type);
        this.tintsWrapper.hidden = type === '';
        this.tintsWrapper.dataset.lensType = type;
        this.tintInputs.forEach((input) => {
          const exists = typeVariants.some((variant) => variant.tint === input.value);
          const swatch = input.closest('[data-lens-tint-option]');
          swatch.hidden = !exists;
          input.disabled = !exists;
          if (!exists) input.checked = false;

          // Display name can differ by lens type (e.g. Green shows as Blue/Green under Mirror).
          const label = this.tintDetails(input, type).label;
          swatch.dataset.tooltip = label;
          const labelElement = swatch.querySelector('[data-lens-tint-label]');
          if (labelElement) labelElement.textContent = label;
        });

        const tint = this.checkedValue(this.tintInputs);
        const chosenTintInput = this.tintInputs.find((input) => input.checked);
        const details = chosenTintInput ? this.tintDetails(chosenTintInput, type) : null;
        if (this.tintNameElement) {
          const label = details ? details.label : tint;
          this.tintNameElement.textContent = tint ? label : '';
        }

        this.updateExample(chosenTintInput, details);

        const matchedVariant = typeVariants.find((variant) => variant.tint === tint);
        if (matchedVariant && matchedVariant.available !== false) {
          this.variantInput.value = matchedVariant.id;
          this.updateTotal(this.framePrice + matchedVariant.price);
        } else {
          this.variantInput.value = '';
          this.updateTotal(this.framePrice);
        }

        // A lens is chosen: flag it (hides the Buy-it-now button, which would skip the lens) and, for
        // prescription lenses, attach the "we'll email you" note to the cart line.
        const hasLens = this.variantInput.value !== '';
        this.toggleAttribute('data-lens-selected', hasLens);
        if (this.noteInput) this.noteInput.value = hasLens && rx !== 'No RX' ? this.prescriptionNote : '';
      }

      // Display name, photo and caption for a tint. A "Type / Tint" entry in the tint_reference
      // metafield (e.g. "Mirror / Green") overrides the plain tint's details under that lens type.
      tintDetails(tintInput, type) {
        let overrides = {};
        try {
          overrides = JSON.parse(tintInput.dataset.tintOverrides || '{}');
        } catch (e) {
          overrides = {};
        }
        const override = overrides[type] || {};
        return {
          label: override.label || tintInput.value,
          photo: override.photo || tintInput.dataset.tintPhoto || '',
          source: override.source || tintInput.dataset.tintSource || '',
        };
      }

      // Example photo for the chosen tint; hidden when the tint has none.
      updateExample(tintInput, details) {
        if (!this.exampleElement) return;
        const photo = details ? details.photo : '';
        if (!photo) {
          this.exampleElement.hidden = true;
          return;
        }
        if (this.exampleImage.getAttribute('src') !== photo) this.exampleImage.src = photo;
        this.exampleImage.alt = `Example of ${details.label} lenses`;
        const source = details.source;
        this.exampleCaption.textContent = source ? `Example: ${source}` : '';
        this.exampleElement.hidden = false;
      }

      updateTotal(cents) {
        if (this.totalElement) this.totalElement.textContent = formatMoney(cents, this.moneyFormat);
      }
    }
  );
}

function formatMoney(cents, format) {
  const amount = (Math.max(cents, 0) / 100).toFixed(2);
  const [dollars, decimals] = amount.split('.');
  const dollarsWithComma = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  return format
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, dollarsWithComma)
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${dollarsWithComma}.${decimals}`)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, dollars)
    .replace(/\{\{\s*amount\s*\}\}/, `${dollars}.${decimals}`);
}
